import React, { useMemo, useState } from 'react';
import { DateFilters, PlatesToImageCols, getHeaderDeleteButton } from './utils';
import { ImagingSchedulesDeletedDataDTO, PlatesToImageDTO } from 'business/models/Models';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { setImagingSchedulesIsDirty } from 'redux/reducers/imagingSchedulesReducer';
import { useDispatch } from 'react-redux';
import { FmlxTable } from 'fmlx-common-ui';
import { sortTableRows } from 'utils/Global';
import { useFmlxTablePagination, useTableSort } from './hooks';
import { NoTableDataPlaceholder } from './NoTableDataPlaceholder';
import './Imaging.scss';
interface PlatesToImageProps {
  platesToImage: PlatesToImageDTO[];
  setPlatesToImage: React.Dispatch<React.SetStateAction<PlatesToImageDTO[]>>;
  setDeletedData: React.Dispatch<React.SetStateAction<ImagingSchedulesDeletedDataDTO>>;
}

function PlatesToImage(props: PlatesToImageProps) {
  const { platesToImage, setPlatesToImage, setDeletedData } = props;
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('all');
  const { sortState, onTableSorted } = useTableSort(() => setSelectedIds([]));
  const dispatch = useDispatch();

  const tableData = useMemo(() => {
    if (platesToImage) {
      let rows;
      if (selectedOption === 'all') {
        rows = platesToImage?.map((p: PlatesToImageDTO) => ({
          id: p.id,
          dateToImage: p.dateToImage,
          user: p.user,
          experimentName: p.experimentName,
          experimentId: p.experimentId,
          plateNumber: p.plateNumber,
        }));
      } else {
        rows = platesToImage
          ?.filter((item) => {
            const days = parseInt(selectedOption, 10);
            const currentDate = new Date();
            const itemDate = new Date(item.dateToImage);
            const timeDiff = currentDate.getTime() - itemDate.getTime();
            const diffDays = timeDiff / (1000 * 3600 * 24);
            return diffDays <= days;
          })
          .map((p: PlatesToImageDTO) => ({
            id: p.id,
            dateToImage: p.dateToImage,
            user: p.user,
            experimentName: p.experimentName,
            experimentId: p.experimentId,
            plateNumber: p.plateNumber,
          }));
      }
      return sortTableRows(rows ?? [], sortState);
    }
    return [];
  }, [platesToImage, selectedOption, sortState]);

  const { toPageRowIndexes, paginationProps } = useFmlxTablePagination(tableData, {
    resetKey: selectedOption,
    onPageChange: () => setSelectedIds([]),
  });

  const onDelete = () => {
    const ids = selectedIds
      .map((index) => tableData[index]?.id)
      .filter((id): id is number => typeof id === 'number');
    if (ids.length === 0) {
      return;
    }
    const newData = platesToImage.filter((x) => !ids.includes(x.id));
    setPlatesToImage(newData);
    setDeletedData(
      (prevData) =>
        new ImagingSchedulesDeletedDataDTO({
          ...prevData,
          imagingTaskClearedFromToImageIds: Array.from(
            new Set([...(prevData?.imagingTaskClearedFromToImageIds || []), ...ids]),
          ),
        }),
    );
    dispatch(setImagingSchedulesIsDirty(true));
    setSelectedIds([]);
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    setSelectedOption(selectedValue);
    setSelectedIds([]);
  };

  // const onCheckboxSelectionChange = (rowIndices: number[]) => {
  //   setSelectedIds([...rowIndices]);
  // };

  const handleOnRowCLick = (row) => {
    const rowIndex = tableData.findIndex((item) => item.id === row.id);
    setSelectedIds(rowIndex >= 0 ? [rowIndex] : []);
  };

  const handleRowDoubleClick = (row: any) => {
    window.open(`/exp/${row.experimentId}`, `/exp/${row.experimentId}`);
  };

  const deleteButton = getHeaderDeleteButton(onDelete, selectedIds?.length > 0);

  return (
    <div id="platestoimage-wrapper" className="flex flex-col h-[90%]">
      <div
        id="platestoimage-container"
        className="flex flex-row items-center justify-between py-2"
      >
        <div id="platestoimage-title" className="text-lg font-bold mx-2">
          Plates To Image
        </div>
        <div
          id="platestoimage-lists"
          className="flex flex-row text-lg font-bold mx-2 gap-2"
        >
          <RMCustomButton {...deleteButton} />
          <select
            id="platestoimage-option"
            value={selectedOption}
            onChange={(e) => handleSelectChange(e)}
          >
            {DateFilters.map((f, i) => (
              <option
                id={`platestoimage-option-datefilter${i}`}
                key={`df${i}`}
                value={f.value}
              >
                {f.title}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="imaging-paginated-table relative min-h-0 flex-1">
        <FmlxTable
          key={selectedOption}
          id="plate-to-image-table"
          columns={PlatesToImageCols}
          selectedRowIds={toPageRowIndexes(selectedIds)}
          onRowClick={handleOnRowCLick}
          onRowDoubleClick={handleRowDoubleClick}
          getSortValue={onTableSorted}
          placeholder={null}
          {...paginationProps}
        />
        {tableData.length === 0 && <NoTableDataPlaceholder />}
      </div>
    </div>
  );
}

export default PlatesToImage;
